import type { IFileItem } from '../models/FileItem';
import type { IFolderItem } from '../models/FolderItem';
import type { IOrganizationPlan, IPlannedChange } from '../models/Plan';
import type { IScanResult } from '../models/ScanResult';
import { isCatchAllFolderName } from '../rules/FolderRules';
import { fileBaseName, values } from '../rules/ruleUtils';
import { quarantineStamp, QUARANTINE_FOLDER } from '../plan/PlanGenerator';

/** Words that say nothing about where a file belongs. */
const STOP_WORDS: { [word: string]: boolean } = {
  the: true, and: true, for: true, with: true, from: true, copy: true, final: true, new: true, draft: true,
  latest: true, updated: true, document: true, doc: true, file: true, scan: true, img: true, image: true,
  photo: true, untitled: true, version: true
};

/** Below this score a suggestion is too weak to show; the file is marked "needs your input". */
const MIN_SCORE: number = 0.4;
/** Intake suggestions never claim high confidence: a person always decides. */
const MAX_CONFIDENCE: number = 0.9;

function singular(word: string): string {
  return word.length > 3 && /s$/.test(word) && !/ss$/.test(word) ? word.slice(0, -1) : word;
}

/** Meaningful words in a name: lower-case, no numbers, dates, short words or filler. */
export function nameTokens(name: string): string[] {
  const words: string[] = name.toLowerCase().split(/[^a-z]+/).filter(w => w.length >= 3 && !STOP_WORDS[w]).map(singular);
  return words.filter((w, i) => words.indexOf(w) === i);
}

function overlap(a: string[], b: string[]): number {
  if (a.length === 0 || b.length === 0) {
    return 0;
  }
  const shared: number = a.filter(w => b.indexOf(w) !== -1).length;
  return shared / (a.length + b.length - shared);
}

export interface IIntakeSuggestion {
  folder: IFolderItem;
  /** 0–1. */
  confidence: number;
  reason: string;
}

function isInCatchAll(scan: IScanResult, folder: IFolderItem): boolean {
  for (let f: IFolderItem | undefined = folder; f && f.id !== scan.rootId; f = f.parentId ? scan.folders[f.parentId] : undefined) {
    if (isCatchAllFolderName(f.name)) {
      return true;
    }
  }
  return false;
}

/**
 * Where a new file most likely belongs, learned from the library itself: files with similar
 * names, the same file type, and folder names that match words in the file's name.
 * Catch-all folders (Misc, Old…) are never suggested. Undefined when nothing fits well.
 */
export function suggestDestination(scan: IScanResult, file: IFileItem): IIntakeSuggestion | undefined {
  const tokens: string[] = nameTokens(fileBaseName(file));
  if (tokens.length === 0) {
    return undefined;
  }

  let best: { folder: IFolderItem; score: number; similarFile?: IFileItem; similarity: number; folderWords: string[] } | undefined;
  for (const folder of values(scan.folders)) {
    if (folder.id === scan.rootId || isInCatchAll(scan, folder)) {
      continue;
    }
    const folderFiles: IFileItem[] = folder.fileIds.map(id => scan.files[id]);

    let similarity: number = 0;
    let similarFile: IFileItem | undefined;
    for (const other of folderFiles) {
      const s: number = overlap(tokens, nameTokens(fileBaseName(other)));
      if (s > similarity) {
        similarity = s;
        similarFile = other;
      }
    }
    const sameType: number = folderFiles.length > 0
      ? folderFiles.filter(f => f.extension === file.extension).length / folderFiles.length
      : 0;
    const pathWords: string[] = nameTokens(folder.path.replace(/\//g, ' '));
    const folderWords: string[] = tokens.filter(w => pathWords.indexOf(w) !== -1);
    const nameMatch: number = folderWords.length / tokens.length;

    const score: number = 0.6 * similarity + 0.15 * sameType + 0.25 * nameMatch;
    const better: boolean = !best || score > best.score + 1e-9 ||
      (Math.abs(score - best.score) <= 1e-9 && folder.fileIds.length > best.folder.fileIds.length);
    if (better) {
      best = { folder, score, similarFile, similarity, folderWords };
    }
  }

  if (!best || best.score < MIN_SCORE) {
    return undefined;
  }
  const reasons: string[] = [];
  if (best.similarFile && best.similarity >= 0.34) {
    reasons.push(`it has a similar file, "${best.similarFile.name}"`);
  }
  if (best.folderWords.length > 0) {
    reasons.push(`the folder name matches "${best.folderWords.join('", "')}"`);
  }
  return {
    folder: best.folder,
    confidence: Math.min(MAX_CONFIDENCE, Math.round(best.score * 100) / 100),
    reason: `Suggested because ${reasons.join(' and ') || 'files of this type are kept there'}.`
  };
}

/** The scan with the _Intake folder and its files added, so the plan, preview and engine can see them. */
export function withIntake(scan: IScanResult): IScanResult {
  if (!scan.intake) {
    return scan;
  }
  const folders: IScanResult['folders'] = { ...scan.folders, [scan.intake.folder.id]: scan.intake.folder };
  const files: IScanResult['files'] = { ...scan.files };
  for (const f of scan.intake.files) {
    files[f.id] = f;
  }
  return { ...scan, folders, files };
}

/** One "file from intake" change per file waiting in _Intake. */
export function generateIntakePlan(scan: IScanResult): IOrganizationPlan {
  const changes: IPlannedChange[] = (scan.intake?.files ?? []).map(file => {
    const suggestion: IIntakeSuggestion | undefined = suggestDestination(scan, file);
    return {
      id: `fileIntake:${file.id}`,
      type: 'fileIntake',
      itemKind: 'file',
      targetId: file.id,
      suggestedParentPath: suggestion?.folder.path,
      needsInput: !suggestion,
      reason: suggestion
        ? suggestion.reason
        : 'No folder in the library looks like a good fit. Choose where this file belongs.',
      confidence: suggestion ? suggestion.confidence : 0,
      issueIds: []
    };
  });
  return { changes, quarantinePath: `${QUARANTINE_FOLDER}/${quarantineStamp(scan.scannedAt)}` };
}
